import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';

// Contact details and social links. The built-in values show at once; the database values replace them when they arrive,
// so the header, footer and contact blocks never wait for the network.
const defaults = {
  contact: {
    address: 'Paragammana, Hettimulla, Kegalle',
    addressFull: 'SANASA Campus Ltd, Paragammana, Hettimulla, Kegalle, Sri Lanka',
    generalPhone: '(+94) 35 492 77 78',
    generalPhoneHref: 'tel:+94354927778',
    email: 'info@sanasacampus.lk',
    admissionsPhone: '(+94) 35 493 51 26',
    admissionsPhoneHref: 'tel:+94354935126',
    admissionsEmail: 'asst.ar.ac@gmail.com',
    cccsPhone: '(+94) 71 206 32 05',
    cccsPhoneHref: 'tel:+94712063205',
    cccsEmail: 'sanasacampuscc@gmail.com',
  },
  social: {
    facebook: 'https://www.facebook.com/Sanasa-Campus-Kegalle-Sri-Lanka-610797679056866/',
    instagram: 'https://instagram.com/sanasacampus',
    youtube: 'https://www.youtube.com/@sanasacampus377',
  },
  analytics: { measurementId: null },
  site: { name: 'SANASA Campus Ltd.', tagline: 'Degrees and diplomas in Kegalle' },
};

const SettingsContext = createContext({ ...defaults, contact: { ...defaults.contact, ...defaults.social } });

export const SiteSettingsProvider = ({ children }) => {
  const [rows, setRows] = useState({});

  useEffect(() => {
    let alive = true;
    supabase
      .from('site_settings')
      .select('key, value')
      .then(({ data }) => {
        if (alive && data) setRows(Object.fromEntries(data.map((r) => [r.key, r.value])));
      });
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo(() => {
    const merged = {
      contact: { ...defaults.contact, ...(rows.contact || {}) },
      social: { ...defaults.social, ...(rows.social || {}) },
      analytics: { ...defaults.analytics, ...(rows.analytics || {}) },
      site: { ...defaults.site, ...(rows.site || {}) },
    };
    // Pages read social links from "contact" (contact.facebook), so both are available there.
    return { ...merged, contact: { ...merged.contact, ...merged.social } };
  }, [rows]);

  // Google Analytics: switched on only when an id is saved in the back office. It is not used on the back office itself.
  const measurementId = value.analytics && value.analytics.measurementId;
  useEffect(() => {
    if (!measurementId || !/^G-[A-Z0-9]{6,}$/.test(measurementId)) return;
    if (window.location.pathname.startsWith('/admin') || document.getElementById('ga-script')) return;
    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId);
  }, [measurementId]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSiteSettings = () => useContext(SettingsContext);
